import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { planNameToKey } from "@/mocks/plans";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const { t } = useTranslation("common");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileOpen(false);
  };

  const isLight = isHome ? scrolled : true;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isHome
          ? scrolled
            ? "bg-background-50/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
          : "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/50"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <i className="ri-sparkling-line text-primary-500 text-xl md:text-2xl"></i>
            <span className={`font-heading text-xl md:text-2xl font-semibold transition-colors ${isLight ? "text-foreground-950" : "text-white"}`}>
              Wyzon
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {isHome && (
              <>
                {[
                  { label: t("nav.advantages"), id: "advantages" },
                  { label: t("nav.testimonials"), id: "testimonials" },
                  { label: t("nav.pricing"), id: "pricing", href: "/plans" },
                  { label: t("nav.contact"), id: "contact" },
                ].map((item) =>
                  item.href ? (
                    <Link
                      key={item.id}
                      to={item.href}
                      className={`text-sm font-medium transition-colors hover:opacity-80 ${
                        isLight ? "text-foreground-700" : "text-white/90"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <button
                      key={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`text-sm font-medium transition-colors hover:opacity-80 cursor-pointer ${
                        isLight ? "text-foreground-700" : "text-white/90"
                      }`}
                    >
                      {item.label}
                    </button>
                  )
                )}
              </>
            )}
            {!isHome && (
              <Link
                to="/"
                className={`text-sm font-medium transition-colors hover:opacity-80 ${isLight ? "text-foreground-700" : "text-white/90"}`}
              >
                {t("nav.home")}
              </Link>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <LanguageSwitcher />
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-background-100 border border-background-200 hover:border-background-300 transition-colors cursor-pointer"
                >
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center text-xs font-semibold text-primary-700">
                    {user?.phone?.slice(-2) || "U"}
                  </div>
                  <span className="text-sm text-foreground-700 font-medium">{user?.phone || "User"}</span>
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${profileOpen ? "rotate-180" : ""}`}></i>
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/50 shadow-lg py-2 z-50">
                    <div className="px-4 py-2 border-b border-background-200/50">
                      <p className="text-sm font-semibold text-foreground-950">{user?.phone}</p>
                      <p className="text-xs text-primary-600 font-medium">{t(planNameToKey(user?.plan || ""), { ns: "common" })}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-dashboard-3-line text-sm"></i>
                      {t("nav.dashboard")}
                    </Link>
                    <button
                      onClick={() => {
                        setProfileOpen(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                    >
                      <i className="ri-logout-box-r-line text-sm"></i>
                      {t("nav.logout")}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className={`text-sm font-medium px-5 py-2.5 rounded-full transition-all whitespace-nowrap ${
                  isHome
                    ? isLight
                      ? "bg-primary-500 text-white hover:bg-primary-600"
                      : "bg-white/20 text-white hover:bg-white/30"
                    : "bg-primary-500 text-white hover:bg-primary-600"
                }`}
              >
                {t("nav.signIn")}
              </Link>
            )}
          </div>

          <div className="md:hidden flex items-center gap-1">
            <LanguageSwitcher />
            <button
              className="w-10 h-10 flex items-center justify-center cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={t("nav.toggleMenu")}
            >
              <i className={`ri-menu-line text-xl ${isLight ? "text-foreground-950" : "text-white"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50/98 backdrop-blur-lg border-t border-background-200/50 px-4 py-6 space-y-3">
          {isHome && (
            <>
              {[
                { label: t("nav.advantages"), id: "advantages" },
                { label: t("nav.testimonials"), id: "testimonials" },
                { label: t("nav.pricing"), id: "pricing", href: "/plans" },
                { label: t("nav.contact"), id: "contact" },
              ].map((item) =>
                item.href ? (
                  <Link
                    key={item.id}
                    to={item.href}
                    className="block w-full text-left text-foreground-700 hover:text-foreground-950 text-base font-medium py-2"
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className="block w-full text-left text-foreground-700 hover:text-foreground-950 text-base font-medium py-2 cursor-pointer"
                  >
                    {item.label}
                  </button>
                )
              )}
            </>
          )}
          {!isHome && (
            <Link to="/" className="block text-foreground-700 hover:text-foreground-950 text-base font-medium py-2">
              {t("nav.home")}
            </Link>
          )}
          {isAuthenticated ? (
            <>
              <Link to="/dashboard" className="block text-foreground-700 hover:text-foreground-950 text-base font-medium py-2">
                {t("nav.dashboard")}
              </Link>
              <div className="pt-2 border-t border-background-200/50">
                <p className="text-sm text-foreground-500 py-1">{user?.phone}</p>
                <p className="text-xs text-primary-600 py-1">{t(planNameToKey(user?.plan || ""), { ns: "common" })}</p>
                <button
                  onClick={logout}
                  className="block w-full text-left text-red-600 text-base font-medium py-2 cursor-pointer"
                >
                  {t("nav.logout")}
                </button>
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="block w-full text-center bg-primary-500 text-white text-sm font-medium px-5 py-2.5 rounded-full mt-3"
            >
              {t("nav.signIn")}
            </Link>
          )}

        </div>
      )}
    </nav>
  );
}